import RdcoModalContent from "@affinity/components/ProductList/RdcoModalContent";
import ServiceAccounts from "@affinity/Store/ServiceAccounts";
import { customDispatch } from "@affinity/Store/ServiceBase";
import { defaultShipToCollectionModel, defaultShipToModel } from "@affinity/Store/StoreTypeStates";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { createShipTo, CreateShipToApiParameter, getBillTo, updateBillTo, UpdateBillToApiParameter } from "@insite/client-framework/Services/CustomersService";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { BillToModel, ShipToCollectionModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

// External parameters
interface AddAddressParameters {
    currentUserId: string,
    billToId: string | undefined,
    postcode: string,
    address1: string,
    address2: string,
    address3: string,
    towncity: string,
    county: string,
    rdcoUsageType: number, // 1-6 or 9, default 9? Need Commerce settings to define table of options and default value...
    name: string,
    phone: string | undefined,
}

// Internal parameters
interface Props {
    createdShipTo: ShipToModel
    shipTos: ShipToCollectionModel | undefined,
    updatedShipTo: ShipToModel,
    apiParameter: CreateShipToApiParameter,
    apiResult: ShipToModel,
    billToId: string | undefined,
    billTo: BillToModel | undefined,
    billToResult: BillToModel,
}

type HandlerType = Handler<AddAddressParameters, Props>;

// Build Address
// This method should bind data to the ship to for creation
const BuildAddress: HandlerType = (props) => {
    const add = defaultShipToModel;
    add.postalCode = props.parameter.postcode;
    add.address1 = props.parameter.address1;
    add.address2 = props.parameter.address2;
    add.address3 = props.parameter.address3;
    add.city = props.parameter.towncity;
    add.address4 = props.parameter.county;
    add.customerName = props.parameter.name; // Effectively just a text name for address, customers name on bill to. But behaviour needs to match synch.

    // TEMP IDS - These will change on response from create account
    add.id = props.parameter.currentUserId;
    add.customerNumber = props.parameter.currentUserId;
    add.customerSequence = Date.now().toString(); // Needs to be unique, will clean up server side, should be a way to get from system?

    if (props.parameter.phone) {
        add.phone = props.parameter.phone;
    }

    add.properties = {
        rdcoCode: props.parameter.rdcoUsageType.toString()
    }

    // Store for next step
    props.createdShipTo = add;
};

// Create Address
// API Call
const CreateAddress: HandlerType = async (props) => {
    // IF BILLTO IS EMPTY
    if (props.parameter.billToId) {
        props.billTo = await getBillTo({ billToId: props.parameter.billToId })
    }
    if (props.billTo && !props.billTo.postalCode) {
        const updatedBillTo: BillToModel = {
            ...props.billTo,
            postalCode: props.parameter.postcode,
            address1: props.parameter.address1,
            address2: props.parameter.address2,
            address3: props.parameter.address3,
            city: props.parameter.towncity,
            address4: props.parameter.county,
            customerName: props.parameter.name,
            phone: props.parameter.phone ?? '',
        };
        if (props.parameter.rdcoUsageType)
        {
            updatedBillTo.properties = {
                rdcoCode: props.parameter.rdcoUsageType.toString(), 
            };
        }
        const billToApiParameter: UpdateBillToApiParameter = {
            billTo: updatedBillTo
        }
        props.billToResult = await updateBillTo(billToApiParameter);
        props.billToId = props.billToResult.id;
        // Clone to ship to for ease?
    } else {

        props.apiParameter = {
            billToId: props.parameter.billToId, // Pass current bill To but this may change on first response
            shipTo: props.createdShipTo,
        };

        props.apiResult = await createShipTo(props.apiParameter);
        props.billToId = props.parameter.billToId;
    }
};

export const LoadAddresses: HandlerType = async (props) => {
    if (!props.billToId)
    {
        return;
    }

    props.shipTos = await new ServiceAccounts().getAddresses(props.billToId);
}

// Set Addresses on both forms, just call both dispatches
export const UpdateState: HandlerType = props => {
    if (props.shipTos) {
        (props.dispatch as customDispatch)({
            type: "Affinity/CreateAccount/SetAddresses",
            addressCollection: props.shipTos
        });

        (props.dispatch as customDispatch)({
            type: "Affinity/PlaceOrder/SetAddresses",
            addressCollection: props.shipTos
        });
    }

    (props.dispatch as customDispatch)({
        type: "Affinity/CreateAccount/SwitchAddressMode",
        value: undefined
    });

    (props.dispatch as customDispatch)({
        type: "Affinity/PlaceOrder/SwitchAddressMode",
        value: undefined
    });
}

export const Tracking: HandlerType = props => {
    //
}

export const chain = [
    BuildAddress,
    CreateAddress,
    LoadAddresses, // Refresh for state update
    UpdateState,
    Tracking,
];

const addAddress = createHandlerChainRunner(chain, "AddAddress");
export default addAddress;